import { useMemo } from 'react';

import type {
  PartFileStandaloneViewerData,
  ExtractedTableJson,
  FieldSuggestion,
} from '@frontend/api';
import {
  AssistanceIcon,
  Divider,
  Flex,
  FormLabel,
  Text,
  Tooltip,
  TruncateTextWithTooltip,
  VStack,
} from '@frontend/legacy-components';

import { FindingInlineItem } from 'modules/common/components/findings';
import { useSelectedTable } from 'modules/extractedTables/SelectedTableContext';
import { separateBomsAndTables } from 'modules/extractedTables/utils';

import { useInferenceInfo } from '../hooks/useInferenceInfo';

const FieldSuggestionFindingText = ({
  suggestion,
  fieldTitle,
}: {
  suggestion: FieldSuggestion;
  fieldTitle: string;
}) => {
  return (
    <VStack spacing={0.5} as="li" align="flex-start">
      <FormLabel as="span" m={0} whiteSpace="nowrap" height="auto">
        {fieldTitle}
      </FormLabel>
      <TruncateTextWithTooltip
        text={suggestion.suggestion as string}
        color="feature.primary"
        fontWeight="normal"
        maxWidth="10em"
        fontFamily="default"
      />
    </VStack>
  );
};

const TablePills = ({
  extractedTables,
  label,
}: {
  extractedTables: ExtractedTableJson[];
  label: string;
}) => {
  const { setSelectedTableUrl } = useSelectedTable();
  return (
    <VStack spacing={0.5} as="li" align="flex-start">
      <FormLabel as="span" m={0} height="auto">
        {`${label}s`}
      </FormLabel>
      <Flex gap={0.5}>
        {extractedTables.map((tableJson, index) => {
          return (
            <FindingInlineItem
              key={tableJson.id}
              isOpen={false}
              onClick={() => setSelectedTableUrl(tableJson.authenticatedUrl)}
              whiteSpace="nowrap"
            >
              {`${label} ${index + 1}`}
            </FindingInlineItem>
          );
        })}
      </Flex>
    </VStack>
  );
};

type BaseSuggestionsProps = {
  fieldSuggestions: {
    partNumber?: FieldSuggestion | null;
    revision?: FieldSuggestion | null;
    description?: FieldSuggestion | null;
  };
  extractedTables: ExtractedTableJson[];
};

const getDescriptionsAndPills = ({
  fieldSuggestions,
  extractedTables,
}: BaseSuggestionsProps) => {
  const out = [];
  if (fieldSuggestions.partNumber) {
    out.push(
      <FieldSuggestionFindingText
        suggestion={fieldSuggestions.partNumber}
        key="part-number"
        fieldTitle="Part number"
      />,
    );
  }
  if (fieldSuggestions.revision) {
    out.push(
      <FieldSuggestionFindingText
        suggestion={fieldSuggestions.revision}
        key="revision"
        fieldTitle="Revision"
      />,
    );
  }
  if (fieldSuggestions.description) {
    out.push(
      <FieldSuggestionFindingText
        suggestion={fieldSuggestions.description}
        key="description"
        fieldTitle="Description"
      />,
    );
  }
  const { boms, tables } = separateBomsAndTables(extractedTables);
  if (boms.length > 0) {
    out.push(<TablePills key="boms" extractedTables={boms} label="BOM" />);
  }
  if (tables.length > 0) {
    out.push(
      <TablePills key="tables" extractedTables={tables} label="Table" />,
    );
  }
  return out;
};

type HeaderSuggestionsContentProps = {
  activeFile: PartFileStandaloneViewerData;
  isMobile?: boolean;
};

export const HeaderSuggestionsContent = ({
  isMobile,
}: HeaderSuggestionsContentProps) => {
  const { suggestionsList, extractedTables } = useInferenceInfo();
  const hasTables = extractedTables.length > 0;

  const fieldSuggestions = useMemo(() => {
    if (suggestionsList.data) {
      return {
        partNumber: suggestionsList.data.find(
          (sug) => sug.fieldName == 'part_number',
        ),
        revision: suggestionsList.data.find(
          (sug) => sug.fieldName == 'revision',
        ),
        description: suggestionsList.data.find(
          (sug) => sug.fieldName == 'description',
        ),
      };
    }
    return {
      partNumber: null,
      revision: null,
      description: null,
    };
  }, [suggestionsList.data]);

  const hasSuggestions =
    fieldSuggestions.partNumber ||
    fieldSuggestions.revision ||
    fieldSuggestions.description ||
    hasTables;

  return hasSuggestions ? (
    <>
      <VStack spacing={0} height="100%" pl={1.5}>
        <Divider orientation="vertical" />
        <Tooltip
          placement="top"
          label={
            <Text color="feature.onInteraction">
              <strong>Found in Files</strong> uses AI to find important and
              easy-to-miss content in PDF files.
            </Text>
          }
        >
          <AssistanceIcon size={3} color="interaction.suggestion" />
        </Tooltip>
        <Divider orientation="vertical" />
      </VStack>
      <Flex
        gap={1.5}
        ml={1}
        pt={isMobile ? 0.5 : 0}
        minWidth={0}
        as="ul"
        flexWrap="nowrap"
      >
        {getDescriptionsAndPills({
          fieldSuggestions,
          extractedTables,
        })}
      </Flex>
    </>
  ) : null;
};
